import type { CatalogItem, PriceHistoryItem, PriceHistoryResponse } from '../types/billing';

export function selectBestBillingItem(
  candidates: CatalogItem[],
  practiceId: string
): CatalogItem | null {
  if (!candidates.length) return null;

  const score = (b: CatalogItem) => {
    // Prefer practice-specific overrides over standard items.
    // Then prefer items scoped to the current practice.
    let s = 0;
    if (!b.is_standard) s += 100;
    if (b.practice_id === practiceId) s += 50;
    if (b.is_active) s += 10;
    if (b.based_on_standard_id) s += 5;
    return s;
  };

  return [...candidates].sort((a, b) => score(b) - score(a))[0] ?? null;
}

export type DisplayPriceRow = PriceHistoryItem & { is_standard_reference?: boolean };

export function buildDisplayPrices(params: {
  billingItem: CatalogItem;
  priceHistory: PriceHistoryResponse | null;
  standardItem?: CatalogItem | null;
}): DisplayPriceRow[] {
  const { billingItem, priceHistory, standardItem } = params;

  // Match Billing Catalog: if price history is empty, show current unit_price as an initial row.
  const basePrices: DisplayPriceRow[] =
    priceHistory?.prices && priceHistory.prices.length > 0
      ? (priceHistory.prices as DisplayPriceRow[])
      : billingItem.unit_price > 0
        ? [
            {
              id: 'initial',
              unit_price: billingItem.unit_price,
              is_current: true,
              effective_date: billingItem.created_at,
              created_at: billingItem.created_at,
            },
          ]
        : [];

  // Match Billing Catalog: show the standard price as a reference row for custom overrides.
  if (standardItem) {
    return [
      ...basePrices,
      {
        id: 'standard-reference',
        unit_price: standardItem.unit_price,
        is_current: false,
        is_standard_reference: true,
        effective_date: standardItem.created_at,
        created_at: standardItem.created_at,
      },
    ];
  }

  return basePrices;
}


